<template>
  <div class="gift-package-mall" :style="pageTopStyle">
    <!-- 顶部：定位 + 搜索（吸顶，叠在 Banner 顶色渐变上） -->
    <van-sticky :offset-top="0">
      <head-child class="themed-top-search" :change="true" :theme-color="themeColor" no-right>
        <template #left>
          <div class="themed-top-search__location" @click="selectLocation">
            <van-icon name="location-o" size="0.9rem" />
            <span class="themed-top-search__location-text ellipsis">{{ displayAddress || '定位中' }}</span>
            <van-icon name="arrow-down" size="0.65rem" />
          </div>
        </template>
        <template #middle>
          <van-search
            v-model="searchValue"
            class="themed-top-search__input"
            placeholder="搜索商品"
            shape="round"
            background="transparent"
            readonly
            @focus="onSearchFocus"
          />
        </template>
        <template #right>
          <div class="themed-top-search__service" aria-label="在线客服" @click="openCustomerService">
            <van-icon name="service-o" size="1.4rem" />
          </div>
        </template>
      </head-child>
    </van-sticky>

    <!-- Banner轮播图 推荐使用 2:1 比例（如 750px × 375px） -->
    <div class="banner-section">
      <van-swipe
        ref="bannerSwipe"
        :autoplay="3000"
        :duration="500"
        indicator-color="white"
        class="banner-swipe"
        @change="onSwipeChange"
      >
        <van-swipe-item
          v-for="(banner, index) in bannerList"
          :key="index"
          class="banner-item"
        >
          <div 
            class="banner-content" 
            :style="getBannerStyle(banner)"
          >
          </div>
        </van-swipe-item>
      </van-swipe>
    </div>

    <!-- 公告在 Banner 下方 -->
    <platform-notice-bar page-code="gift_home" />

    <!-- 分类导航 -->
    <div 
      v-if="shouldShowCategorySection"
      class="category-section" 
      :class="{ 'has-bottom-padding': needsCategoryBottomPadding }"
      :style="categorySectionStyle"
    >
      <!-- 一级分类：只有多个一级分类时显示 -->
      <!-- 2-3个时使用grid铺满屏幕，4个以上使用flex可滚动 -->
      <div
        v-if="firstLevelCategories.length > 1"
        class="first-level-categories"
        :class="{ 'first-level-fill': firstLevelCategories.length <= 3 }"
        :style="firstLevelCategories.length <= 3 ? { '--n': firstLevelCategories.length } : {}"
      >
        <div
          v-for="category in firstLevelCategories"
          :key="category.id"
          class="category-item"
          :class="{ active: selectedFirstCategory === category.id }"
          @click="selectFirstCategory(category.id)"
        >
          <div class="category-icon">
            <van-icon :name="category.icon" size="28" />
          </div>
          <span class="category-name">{{ category.name }}</span>
        </div>
      </div>
      
      <!-- 二级分类：选中一级分类时显示容器（使用 v-show 保持 DOM 结构） -->
      <div 
        v-show="selectedFirstCategory"
        class="second-level-categories" 
        :class="{ 
          'only-second-level': firstLevelCategories.length === 1,
          'visible': selectedFirstCategory && (isLoadingSecondCategory(selectedFirstCategory) || getSecondLevelCategories().length > 1),
          'loading': selectedFirstCategory && isLoadingSecondCategory(selectedFirstCategory)
        }"
      >
        <!-- 加载状态：骨架屏 -->
        <div v-if="isLoadingSecondCategory(selectedFirstCategory)" class="second-level-skeleton">
          <div 
            v-for="n in 4" 
            :key="n" 
            class="skeleton-item"
          ></div>
        </div>
        <!-- 数据加载完成：显示二级分类 -->
        <template v-else-if="getSecondLevelCategories().length > 1">
          <div
            v-for="subCategory in getSecondLevelCategories()"
            :key="subCategory.id"
            class="sub-category-item"
            :class="{ active: selectedSecondCategory === subCategory.id }"
            @click="selectSecondCategory(subCategory.id)"
          >
            {{ subCategory.name }}
          </div>
        </template>
      </div>
      
    </div>

    <!-- 排序 -->
    <div class="sort-section">
      <div @click="setNomal" class="sort-left" :style="{ color: queryForm.priceType ? '#333' : '#F00' }">
        默认推荐
      </div>
      <div class="sort-line"></div>
      <div @click="setPrictType" class="sort-right">
        价格
        <van-image
          style="margin-left: 0.2rem;"
          width="0.6rem"
          height="0.8rem"
          :src="queryForm.priceType ? (queryForm.priceType == '1' ? priceup : pricedown) : priceno"
        />
      </div>
    </div>

    <!-- 商品列表 -->
    <div class="product-list">
      <van-pull-refresh v-model="isLoading" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          finished-text="没有更多了"
          @load="onLoad"
        >
          <!-- 加载状态：骨架屏 -->
          <transition name="fade">
            <div v-if="isProductListLoading" key="loading" class="product-list-skeleton">
              <div v-for="n in 6" :key="n" class="skeleton-card">
                <div class="skeleton-image"></div>
                <div class="skeleton-title"></div>
                <div class="skeleton-title short"></div>
                <div class="skeleton-tags">
                  <div class="skeleton-tag"></div>
                  <div class="skeleton-tag"></div>
                </div>
                <div class="skeleton-price"></div>
              </div>
            </div>
          </transition>
          
          <!-- 商品列表：带过渡动画 -->
          <transition name="fade" mode="out-in">
            <goods-list-com 
              v-if="productList.length > 0 && !isProductListLoading" 
              :key="productListKey"
              :DataList="productList"
            ></goods-list-com>
          </transition>
          
          <!-- 空状态 -->
          <transition name="fade">
            <div v-if="productList.length === 0 && !isProductListLoading && !loading" class="empt">
              <!-- 空状态 -->
            </div>
          </transition>
        </van-list>
      </van-pull-refresh>
    </div>

    <!-- 底部导航：根据缓存自动配置 -->
    <AllNav /> 

    <!-- 引入定位组件并监听定位完成事件 -->
    <LocationComponent @location-finish="onLocationFinish" />

    <!-- 返回顶部按钮 -->
    <div 
      v-show="showBackToTop" 
      class="back-to-top" 
      @click="scrollToTop"
    >
      <van-icon name="arrow-up" size="20" color="#fff" />
    </div>
  </div>
</template>

<script>
import AllNav from '@/components/footnav/AllNav.vue'
import LocationComponent from '@/components/map/Location.vue'
import GoodsListCom from '@/components/GoodsListCom.vue'
import HeadChild from '@/components/HeadChild.vue'
import PlatformNoticeBar from '@/components/PlatformNoticeBar.vue'
import { mapState } from 'vuex'
import {
  getHomeTopBackgroundStyle,
  GIFT_PACKAGE_DEFAULT_TOP_COLOR,
  setStoredGiftPackageBannerThemeColor,
} from '@/utils/themeColor'
import { openCustomerService } from '@/utils/customerService'

export default {
  name: 'GiftPackageHome',
  components: {
    AllNav,
    LocationComponent,
    GoodsListCom,
    HeadChild,
    PlatformNoticeBar,
  },
  data() {
    return {
      searchValue: '',
      selectedFirstCategory: null,
      selectedSecondCategory: null,
      themeColor: '#667eea',
      loading: false,
      finished: false,
      isLoading: false, // 下拉刷新状态
      isFirstLoad: true,
      loadingSecondCategory: {}, // 标记正在加载的二级分类，避免重复请求
      retryCount: 0, // 重试计数器（一级分类）
      retryCountSecond: {}, // 重试计数器（二级分类，按categoryId存储）
      showBackToTop: false,
      scrollTimer: null, // 滚动节流定时器
      selectCategoryTimer: null, // 选择分类防抖定时器
      isProductListLoading: false, // 商品列表是否正在加载（用于显示骨架屏）
      productListKey: 0, // 商品列表的 key，用于强制重新渲染
      priceup: require('@/assets/images/price-up.png'),
      pricedown: require('@/assets/images/price-down.png'),
      priceno: require('@/assets/images/price-no.png'),
      bannerList: [], // banner数据
      currentBannerIndex: 0,
      // 一级分类（从接口获取）
      firstLevelCategories: [],
      // 二级分类（从接口获取，按一级分类id分组）
      secondLevelCategories: {},
      // 接口需要的参数
      bathId: '',
      upgradeFlag: '',
      // 商品列表（从接口获取）
      productList: [],
      // 查询表单
      queryForm: {
        cardId: localStorage.getItem("cardId") || "",
        current: 1, // 当前页码
        size: 10, // 每页个数
        productName: "", // 商品名称（搜索用）
        category1Id: "", // 一级分类ID
        category2Id: "", // 二级分类ID
        cityCode: '', // 城市代码
        upgradeFlag: '', // 升级标识
        bathId: '', // 批次ID
        id: '', // 活动ID
        hotType: '', // 热门类型
        priceType: null, // 价格排序：null 默认推荐，1 升序，0 降序
        // 礼包首页固定只查礼包商品（ProductTypeEnum.GIFT=10）；勿用 type，后端 type 表示 C1/C2
        productType: '10'
      }
    }
  },
  computed: {
    // 获取定位地址信息
    ...mapState({
      address: state => state.address,
      cityCode: state => state.cityCode
    }),
    // 显示地址（超过4个字显示省略号）
    displayAddress() {
      if (!this.address) return ''
      if (this.address.length > 4) {
        return this.address.substring(0, 4) + '...'
      }
      return this.address
    },
    categorySectionStyle() {
      const start = this.bannerBottomThemeColor
      const end = this.lighten(this.bannerBottomThemeColor, 0.35)
      return {
        background: `linear-gradient(135deg, ${start} 0%, ${end} 100%)`
      }
    },
    bannerBottomThemeColor() {
      if (!this.bannerList || !this.bannerList.length) return this.themeColor
      const banner = this.bannerList[this.currentBannerIndex] || this.bannerList[0]
      return (banner && (banner.themeColorBottom || banner.themeColor)) || this.themeColor
    },
    // 判断是否有二级分类显示
    hasSecondLevelDisplay() {
      return this.selectedFirstCategory && this.getSecondLevelCategories().length > 1
    },
    // 判断是否需要显示分类区域
    shouldShowCategorySection() {
      // 多个1级分类时显示
      if (this.firstLevelCategories.length > 1) return true
      // 只有1个1级分类时，选中且有多个2级分类时显示
      if (this.firstLevelCategories.length === 1 && this.selectedFirstCategory) {
        return this.getSecondLevelCategories().length > 1
      }
      return false
    },
    needsCategoryBottomPadding() {
      // 有二级分类显示时不需要下边距
      if (this.hasSecondLevelDisplay) return false
      // 多个一级分类显示时需要下边距
      return this.firstLevelCategories.length > 1
    },
    pageTopStyle() {
      // 覆盖顶栏 + Banner 区域，与商城首页同样取图顶部色渐变
      return getHomeTopBackgroundStyle(
        this.themeColor,
        'calc(3rem + 200px)',
        GIFT_PACKAGE_DEFAULT_TOP_COLOR
      )
    },
  },
  methods: {
    // 获取banner样式对象
    getBannerStyle(banner) {
      if (!banner) return {}
      
      // 如果有图片URL，设置背景图片样式
      if (banner.imgUrl && banner.imgUrl.trim()) {
        return {
          backgroundImage: `url(${banner.imgUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      }
      
      // 如果有背景样式（可能是渐变色），直接使用
      if (banner.background) {
        return {
          background: banner.background
        }
      }
      
      return {}
    },
    selectLocation() {
      this.$router.push('/SelectAdderss')
    },
    // 顶部搜索栏客服入口
    openCustomerService,
    /**
     * 定位完成后的回调
     * @param {Object} locationInfo 定位信息
     */
    onLocationFinish(locationInfo) {
      // 更新城市代码（通过 Vuex mutation）
      if (locationInfo.cityCode && locationInfo.cityCode !== this.cityCode) {
        this.$store.commit('setCityCode', locationInfo.cityCode)
      }
    },
    // 获取banner数据
    getBanner() {
      // 确保参数已准备好
      if (!this.bathId || !this.upgradeFlag || !this.cityCode) {
        return
      }
      
      let form = {
        cardId: localStorage.getItem("cardId") || '',
        cityCode: this.cityCode,
        upgradeFlag: this.upgradeFlag,
        bathId: this.bathId,
        hotType: "A",
        showPositionCode: "3"
      }

      this.$api
        .getQueryRotation(form)
        .then((res) => {
          if (res.code === 0 && res.data && res.data.length > 0) {
            // 将接口返回的图片数据转换为bannerList格式
            this.bannerList = res.data.map((item) => {
              const imgUrl = item.showImgId?.showImg || ''
              
              return {
                imgUrl: imgUrl,
                background: imgUrl ? `url(${imgUrl}) center/cover no-repeat` : '',
                image: imgUrl,
                themeColor: item.themeColor || '#667eea',
                themeColorBottom: item.themeColorBottom || item.themeColor || '#667eea',
              }
            })
            
            // banner数据加载后，更新主题色
            this.$nextTick(() => {
              this.updateThemeFromBanner(0)
            })
          }
        })
        .catch((err) => {
          console.error('获取banner失败:', err)
        })
    },
    /**
     * 选择一级分类（带防抖处理）
     */
    selectFirstCategory(categoryId) {
      // 清除之前的防抖定时器
      if (this.selectCategoryTimer) {
        clearTimeout(this.selectCategoryTimer)
        this.selectCategoryTimer = null
      }
      
      // 如果点击的是已选中的分类，则取消选中（不需要防抖）
      if (this.selectedFirstCategory === categoryId) {
        this.selectedFirstCategory = null
        this.selectedSecondCategory = null
        this.queryForm.category1Id = ''
        this.queryForm.category2Id = ''
        this.finished = false
        // 刷新商品列表
        this.onRefresh()
        return
      }
      
      // 防抖处理：200ms 内只响应最后一次点击
      this.selectCategoryTimer = setTimeout(() => {
        // 选中新的分类
        this.selectedFirstCategory = categoryId
        this.selectedSecondCategory = null // 清空二级分类选择
        
        // 更新查询参数
        this.queryForm.category1Id = categoryId || ''
        this.queryForm.category2Id = '' // 切换一级分类时，清空二级分类
        this.finished = false // 重置完成状态
        
        // 获取二级分类
        if (categoryId) {
          this.getSecondLevelCategoriesFromApi(categoryId)
        } else {
          // 如果取消选择，清空二级分类列表
          this.secondLevelCategories = {}
        }
        
        // 刷新商品列表
        this.onRefresh()
        
        this.selectCategoryTimer = null
      }, 200) // 200ms 防抖延迟
    },
    /**
     * 选择二级分类
     */
    selectSecondCategory(subCategoryId) {
      // 如果点击的是已选中的分类，则取消选中
      if (this.selectedSecondCategory === subCategoryId) {
        this.selectedSecondCategory = null
        this.queryForm.category2Id = ''
        this.queryForm.current = 1 // 重置页码
        this.finished = false // 重置完成状态
        this.isFirstLoad = false // 不是首次加载
        this.productList = [] // 清空列表，避免追加
        // 刷新商品列表
        this.getProductList()
        return
      }
      
      // 选中新的分类
      this.selectedSecondCategory = subCategoryId
      
      // 更新查询参数
      this.queryForm.category2Id = subCategoryId || ''
      this.queryForm.current = 1 // 重置页码
      this.finished = false // 重置完成状态
      this.isFirstLoad = false // 不是首次加载
      this.productList = [] // 清空列表，避免追加
      
      // 刷新商品列表
      this.getProductList()
    },
    getSecondLevelCategories() {
      if (!this.selectedFirstCategory) return []
      return this.secondLevelCategories[this.selectedFirstCategory] || []
    },
    /**
     * 判断指定一级分类的二级分类是否正在加载
     */
    isLoadingSecondCategory(categoryId) {
      if (!categoryId) return false
      return this.loadingSecondCategory[categoryId] === true
    },
    /**
     * 预加载所有一级分类的二级分类（优化：减少切换时的等待时间）
     */
    preloadAllSecondCategories() {
      if (!this.firstLevelCategories || this.firstLevelCategories.length === 0) return
      
      // 遍历所有一级分类，预加载二级分类
      this.firstLevelCategories.forEach(category => {
        // 如果还没有加载过，且不在加载中，则预加载
        if (!this.secondLevelCategories[category.id] && 
            !this.loadingSecondCategory[category.id]) {
          this.getSecondLevelCategoriesFromApi(category.id)
        }
      })
    },
    // 获取一二级分类
    getFirstLevelCategoriesFromApi() {
      // 确保 bathId、upgradeFlag 和 cityCode 已加载
      if (!this.bathId || !this.upgradeFlag || !this.cityCode) {
        // 如果参数未准备好，延迟调用（最多重试3次）
        if (!this.retryCount) this.retryCount = 0
        if (this.retryCount < 3) {
          this.retryCount++
          this.$nextTick(() => {
            if (this.bathId && this.upgradeFlag && this.cityCode) {
              this.retryCount = 0
              this.getFirstLevelCategoriesFromApi()
            } else {
              this.getFirstLevelCategoriesFromApi()
            }
          })
        } else {
          this.retryCount = 0
          console.warn('获取分类参数未准备好，已停止重试')
        }
        return
      }
      
      this.retryCount = 0 // 重置重试计数
      this.$api
        .selectCategoryWithChildren(this.bathId, this.upgradeFlag, this.cityCode, 'GIFT')
        .then((res) => {
          if (res.code === 0 && res.data && res.data.length > 0) {
            // 映射接口数据到页面格式
            this.firstLevelCategories = res.data.map((item, index) => ({
              id: item.id,
              name: item.name,
              icon: item.icon,
              children: item.children || []
            }))
            
            // 预填充二级分类数据
            res.data.forEach(item => {
              if (item.children && item.children.length > 0) {
                this.$set(this.secondLevelCategories, item.id, item.children.map(child => ({
                  id: child.id,
                  name: child.name,
                  parentId: child.pid || child.parentId || item.id
                })))
              } else {
                this.$set(this.secondLevelCategories, item.id, [])
              }
            })
            
            // 如果只有1个一级分类，自动选中
            if (this.firstLevelCategories.length === 1) {
              const firstCategory = this.firstLevelCategories[0]
              this.selectedFirstCategory = firstCategory.id
              this.queryForm.category1Id = firstCategory.id
            }
            // 默认不选中任何分类，显示全部分类下的商品
          }
        })
        .catch((err) => {
          console.error('获取分类失败:', err)
        })
    },
    // 获取二级分类（从本地缓存数据）
    getSecondLevelCategoriesFromApi(categoryId) {
      if (!categoryId) return
      
      // 如果已经有缓存数据，直接返回（一次获取时已预填充）
      if (this.secondLevelCategories[categoryId] && this.secondLevelCategories[categoryId].length >= 0) {
        return
      }
      
      // 从一级分类中查找children
      const category = this.firstLevelCategories.find(c => c.id === categoryId)
      if (category && category.children && category.children.length > 0) {
        this.$set(this.secondLevelCategories, categoryId, category.children.map(child => ({
          id: child.id,
          name: child.name,
          parentId: child.pid || child.parentId || categoryId
        })))
      } else {
        this.$set(this.secondLevelCategories, categoryId, [])
      }
    },
    // 主题色：使用后端返回的主题色
    updateThemeFromBanner(index = 0) {
      if (!this.bannerList || this.bannerList.length === 0) return
      const banner = this.bannerList[index] || this.bannerList[0]
      const color = (banner && banner.themeColor) ? banner.themeColor : GIFT_PACKAGE_DEFAULT_TOP_COLOR
      this.themeColor = color
      this.currentBannerIndex = index
      setStoredGiftPackageBannerThemeColor(color, index)
    },
    syncBannerThemeFromCarousel() {
      this.$nextTick(() => {
        const swipe = this.$refs.bannerSwipe
        if (swipe && typeof swipe.active === 'number') {
          this.currentBannerIndex = swipe.active
        }
        this.updateThemeFromBanner(this.currentBannerIndex)
      })
    },
    onSwipeChange(index) {
      this.currentBannerIndex = index
      this.updateThemeFromBanner(index)
    },
    lighten(hex, amount = 0.2) {
      // hex -> lighten by amount (0-1)
      let c = hex.replace('#', '')
      if (c.length === 3) c = c.split('').map(x => x + x).join('')
      const num = parseInt(c, 16)
      let r = (num >> 16) & 0xff
      let g = (num >> 8) & 0xff
      let b = num & 0xff
      r = Math.min(255, Math.round(r + (255 - r) * amount))
      g = Math.min(255, Math.round(g + (255 - g) * amount))
      b = Math.min(255, Math.round(b + (255 - b) * amount))
      const toHex = v => v.toString(16).padStart(2, '0')
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`
    },
    /**
     * 获取商品列表数据
     */
    getProductList() {
      // 更新查询表单的参数
      this.queryForm.cityCode = this.cityCode
      this.queryForm.upgradeFlag = this.upgradeFlag
      this.queryForm.bathId = this.bathId
      
      // 优化：如果关键参数缺失，不发送请求
      if (!this.cityCode || !this.bathId) {
        console.warn('获取商品列表参数不完整，已跳过请求')
        this.loading = false
        this.isProductListLoading = false
        return
      }
      
      // 如果是第一页，显示加载状态
      if (this.queryForm.current === 1) {
        this.isProductListLoading = true
      }
      
      this.$api
        .queryProductInfoHomeNew(this.queryForm)
        .then((res) => {
          if (res.code == 1) {
            this.$toast(res.msg)
            this.loading = false
            this.finished = true
            this.isProductListLoading = false
            return
          }
          
          // 如果是第一页，替换数据；否则追加数据
          if (this.queryForm.current == 1) {
            // 先更新数据
            this.productList = res.data.records || []
            this.productListKey += 1 // 更新 key，触发过渡动画
            
            // 使用 nextTick 确保 DOM 更新完成后再隐藏加载状态
            this.$nextTick(() => {
              // 延迟隐藏加载状态，让过渡动画更平滑
              setTimeout(() => {
                this.isProductListLoading = false
              }, 200) // 200ms 延迟，确保淡入动画完成
            })
          } else {
            this.productList.push(...(res.data.records || []))
            this.isProductListLoading = false
          }
          
          // 请求完成后，必须设置 loading = false
          this.loading = false
          
          // 判断是否加载完成
          if (this.queryForm.current >= res.data.pages || (res.data.records && res.data.records.length === 0)) {
            this.finished = true
          }
        })
        .catch((err) => {
          console.error('获取商品列表失败:', err)
          this.loading = false
          this.finished = true
          this.isProductListLoading = false
        })
    },
    /**
     * 刷新商品列表（下拉刷新）
     */
    onRefresh() {
      // 优化：减少延迟时间，提升响应速度
      setTimeout(() => {
        this.queryForm.current = 1
        this.finished = false
        this.isFirstLoad = false // 刷新时不是首次加载
        // 不立即清空列表，让过渡动画更平滑
        // 数据加载完成后会自动替换
        this.getProductList()
        this.isLoading = false // 关闭下拉刷新状态
      }, 300) // 优化：从500ms减少到300ms
    },
    /**
     * 加载更多（van-list 的 load 事件）
     */
    onLoad() {
      // 如果已经加载完成，直接返回
      if (this.finished) {
        this.loading = false
        return
      }
      
      // 首次加载：第一页且列表为空
      if (this.isFirstLoad && this.queryForm.current === 1 && this.productList.length === 0) {
        this.isFirstLoad = false
        this.getProductList()
        return
      }
      
      // 加载更多：当前页有数据且未完成
      if (this.productList.length > 0 && !this.finished) {
        this.queryForm.current += 1
        this.getProductList()
        return
      }
      
      // 其他情况停止加载
      this.loading = false
    },
    /**
     * 搜索商品
     */
    onSearch(value) {
      this.queryForm.productName = value
      this.onRefresh()
    },
    /**
     * 搜索框聚焦
     */
    onSearchFocus() {
      this.$router.push('/SearchGoods')
    },
    /**
     * 设置价格排序
     */
    setPrictType() {
      if (this.queryForm.priceType) {
        if (this.queryForm.priceType == '1') {
          this.queryForm.priceType = '0'
        } else {
          this.queryForm.priceType = '1'
        }
      } else {
        this.queryForm.priceType = '1'
      }
      this.onRefresh()
    },
    /**
     * 设置默认排序
     */
    setNomal() {
      this.queryForm.priceType = null
      this.onRefresh()
    },
    /**
     * 返回顶部
     */
    scrollToTop() {
      // 使用平滑滚动
      try {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      } catch (e) {
        // 兼容性处理：如果不支持 smooth，使用直接滚动
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }
    },
    /**
     * 从福利卡页面进入时刷新数据
     */
    refreshFromCard() {
      sessionStorage.removeItem('giftPackageHome_needRefresh')
      
      // 重置状态
      this.selectedFirstCategory = null
      this.selectedSecondCategory = null
      this.queryForm.category1Id = ''
      this.queryForm.category2Id = ''
      this.queryForm.priceType = null
      this.queryForm.current = 1
      this.finished = false
      this.isFirstLoad = true
      this.productList = []
      
      // 从 localStorage 重新读取福利卡信息
      this.queryForm.cardId = localStorage.getItem("cardId") || ""
      this.bathId = localStorage.getItem("bathId") || ''
      this.upgradeFlag = localStorage.getItem("upgradeFlag") || ''
      this.queryForm.bathId = this.bathId
      this.queryForm.upgradeFlag = this.upgradeFlag
      
      // 重新加载数据
      this.getBanner()
      this.getFirstLevelCategoriesFromApi()
      this.updateThemeFromBanner()
      this.getProductList()
      
      // 重置滚动位置
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'auto' })
      })
    },
    /**
     * 处理滚动事件，控制返回顶部按钮显示/隐藏
     */
    handleScroll() {
      // 使用 requestAnimationFrame 进行节流优化
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer)
      }
      this.scrollTimer = requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
        this.showBackToTop = scrollTop > 300
      })
    }
  },
  beforeRouteEnter(to, from, next) {
    // 从福利卡页面跳转过来时，重新启用 keep-alive 并设置刷新标记
    if (from.path === '/AllCardViews') {
      to.meta.keepAlive = true
      sessionStorage.setItem('giftPackageHome_needRefresh', 'true')
    }
    next()
  },
  activated() {
    // 从福利卡页面进入时刷新数据，从详情页/搜索页返回时保持状态并同步 Banner 主题色
    if (sessionStorage.getItem('giftPackageHome_needRefresh') === 'true') {
      this.refreshFromCard()
      return
    }
    this.syncBannerThemeFromCarousel()
  },
  mounted() {
    // 标记当前是礼包商城（写入缓存）
    localStorage.setItem('mallSource', 'giftPackage')
    // 触发事件通知 AllNav 组件更新
    window.dispatchEvent(new Event('mallSourceChange'))
    
    // 初始化接口参数
    this.bathId = localStorage.getItem("bathId") || ''
    this.upgradeFlag = localStorage.getItem("upgradeFlag") || ''
    
    // 初始化查询表单参数
    this.queryForm.cityCode = this.cityCode
    this.queryForm.upgradeFlag = this.upgradeFlag
    this.queryForm.bathId = this.bathId
    
    // 从福利卡页面进入时刷新数据
    if (sessionStorage.getItem('giftPackageHome_needRefresh') === 'true') {
      this.refreshFromCard()
    } else {
      // 正常流程：加载基础数据，商品列表由 van-list 自动触发
      this.getBanner()
      this.getFirstLevelCategoriesFromApi()
      this.updateThemeFromBanner()
    }
    
    // 添加滚动监听，控制返回顶部按钮显示
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll)
    // 清理滚动定时器
    if (this.scrollTimer) {
      cancelAnimationFrame(this.scrollTimer)
      this.scrollTimer = null
    }
    // 清理防抖定时器
    if (this.selectCategoryTimer) {
      clearTimeout(this.selectCategoryTimer)
      this.selectCategoryTimer = null
    }
  },
  watch: {
    // 监听城市代码变化
    cityCode(newVal, oldVal) {
      // 城市代码变化时刷新数据
      if (newVal && oldVal && newVal !== oldVal) {
        this.queryForm.cityCode = newVal
        this.getBanner()
        this.getFirstLevelCategoriesFromApi()
        this.onRefresh()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/styles/themedTopSearch.scss';

.gift-package-mall {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 60px;
  transition: background-image 0.45s ease;

  .banner-section {
    width: 100%;
    height: 200px;
    position: relative;

    .banner-swipe {
      width: 100%;
      height: 100%;

      .banner-item {
        width: 100%;
        height: 100%;

        .banner-content {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;

        }
      }
    }
  }

  .category-section {
    margin: 0;
    padding: 0;
    transition: background 400ms ease, padding-bottom 0.3s ease;
    position: relative;
    z-index: 1;
    
    // 当只显示一级分类时，分类区域需要下边距
    &.has-bottom-padding {
      padding-bottom: 12px; // 下边距12px（与排序区域间距）
    }

    // 一级分类：上边距12px（与banner间距）
    .first-level-categories {
      display: flex;
      gap: 10px;
      padding: 12px 10px 0; // 上边距12px（与banner间距）
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      // 隐藏滚动条但保留滚动功能
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE 10+ */
      
      &::-webkit-scrollbar {
        display: none; /* Chrome Safari */
        width: 0;
        height: 0;
        background: transparent;
      }
      

      .category-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px 10px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        cursor: pointer;
        transition: background 0.3s, color 0.3s, box-shadow 0.3s;
        min-width: 80px !important; // flex布局时最小宽度，使用 !important 防止被压缩
        flex-shrink: 0 !important; // 防止被压缩
        flex-grow: 0 !important; // 防止被拉伸
        box-sizing: border-box; // 确保 padding 包含在宽度内
        // 确保元素位置固定，避免选中时上移
        position: relative;
        margin: 0;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
          // hover 时也不上移，保持位置一致
        }

        &.active {
          background: white;
          color: #20bf6b;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          // 确保选中时位置不变，不使用 transform
          transform: none;
        }

        .category-icon {
          margin-bottom: 0px;
        }

        .category-name {
          font-size: 13px;
          font-weight: 600;
          text-align: center;
          white-space: nowrap;
        }
      }
    }

    .first-level-categories.first-level-fill {
      display: grid;
      grid-template-columns: repeat(var(--n), 1fr);
      gap: 10px;
      padding: 12px 10px 0; // 上边距12px（与banner间距）
      overflow: hidden; // grid布局时（2-3个分类）不需要滚动
      // 隐藏滚动条但保留滚动功能
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE 10+ */
      
      &::-webkit-scrollbar {
        display: none; /* Chrome Safari */
        width: 0;
        height: 0;
        background: transparent;
      }
      
      // grid布局时（2-3个分类），分类项会自动铺满，不需要设置最小宽度
      .category-item {
        min-width: unset !important; // grid布局时不需要最小宽度限制
        max-width: unset !important; // grid布局时不需要最大宽度限制
        flex-shrink: unset !important; // grid布局时不需要flex属性
        flex-grow: unset !important; // grid布局时不需要flex属性
      }
    }

    // 二级分类间距逻辑（优化：添加平滑过渡和固定高度）
    .second-level-categories {
      display: flex;
      gap: 8px;
      padding: 0 10px;
      overflow: hidden; // 改为 hidden，使用 max-height 控制显示
      -webkit-overflow-scrolling: touch;
      
      // 初始状态：隐藏（高度为0，透明度为0）
      max-height: 0;
      opacity: 0;
      margin-top: 0;
      padding-top: 0;
      padding-bottom: 0;
      transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.3s ease,
                  margin-top 0.3s ease,
                  padding-top 0.3s ease,
                  padding-bottom 0.3s ease;
      
      // 场景1：只有1个一级分类时（一级分类不显示），二级分类上边距与banner间距12px
      &.only-second-level {
        &.visible {
          padding: 12px 10px 12px; // 上边距12px（与banner间距），下边距12px（与排序区域间距）
        }
      }
      
      // 场景2：多个一级分类时，二级分类上边距与一级分类间距12px
      &:not(.only-second-level) {
        &.visible {
          margin-top: 12px; // 上边距12px（与一级分类间距）
          padding-top: 0;
          padding-bottom: 12px; // 下边距12px（与排序区域间距）
        }
      }
      
      // 显示状态：展开（使用 max-height 实现平滑展开）
      &.visible {
        max-height: 150px; // 根据实际内容调整，确保能容纳所有二级分类
        opacity: 1;
        overflow-x: auto; // 内容超出时可以横向滚动
        overflow-y: hidden;
      }
      
      // 加载状态：保持最小高度，避免闪烁
      &.loading {
        min-height: 40px;
        max-height: 150px; // 加载时也保持最大高度
        opacity: 0.8;
      }
      
      // 隐藏滚动条但保留滚动功能
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE 10+ */
      
      &::-webkit-scrollbar {
        display: none; /* Chrome Safari */
        width: 0;
        height: 0;
        background: transparent;
      }

      // 二级分类项样式
      .sub-category-item {
        padding: 6px 12px; // 适中的内边距，保持美观
        background: rgba(255, 255, 255, 0.2);
        border-radius: 20px;
        font-size: 12px;
        color: white;
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.3s;
        flex-shrink: 0; // 防止压缩

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        &.active {
          background: white;
          color: #20bf6b;
          font-weight: 600;
        }
      }
      
      // 骨架屏样式
      .second-level-skeleton {
        display: flex;
        gap: 8px;
        width: 100%;
        padding: 0;
        
        .skeleton-item {
          flex-shrink: 0;
          height: 28px;
          width: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }
      }
    }
    
    // 骨架屏加载动画
    @keyframes skeleton-loading {
      0%, 100% {
        opacity: 0.3;
      }
      50% {
        opacity: 0.6;
      }
    }
    
  }

   .sort-section {
     display: flex;
     flex-direction: row;
     width: 100%;
     background-color: white;
     height: 3rem;
     align-items: center;
     margin: 0; // 移除上下外边距，由上方分类的 margin-bottom 提供间距
     position: relative;

     .sort-left {
       flex: 1;
       text-align: center;
       font-size: 14px;
       cursor: pointer;
     }

     .sort-line {
       background-color: #DDD;
       height: 1.5rem;
       width: 1px;
     }

     .sort-right {
       flex: 1;
       text-align: center;
       font-size: 14px;
       cursor: pointer;
       display: flex;
       align-items: center;
       justify-content: center;
     }

   }

  .product-list {
    padding: 12px 0 0; // 上边距12px（与排序区域间距），左右内边距由 GoodsListCom 组件内部处理
    min-height: 200px; // 保持最小高度，避免布局跳动
    
    .empt {
      display: flex;
      flex-direction: column;
      height: 60vh;
      width: 100%;
      align-items: center;
      justify-content: center;
      color: #999;
    }
    
    // 商品列表骨架屏
    .product-list-skeleton {
      display: flex;
      flex-wrap: wrap;
      margin-left: 0.75rem;
      margin-right: 0.75rem;
      justify-content: space-between;
      
      .skeleton-card {
        flex-basis: 49%;
        width: 9.4375rem;
        margin-bottom: 0.5rem;
        background-color: white;
        border-radius: 0.5rem;
        box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.08);
        padding: 0;
        overflow: hidden;
        
        .skeleton-image {
          width: 100%;
          height: 11.5rem;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }
        
        .skeleton-title {
          height: 1rem;
          margin: 0.5rem 0.75rem;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
          
          &.short {
            width: 60%;
            margin-top: 0.25rem;
          }
        }
        
        .skeleton-tags {
          display: flex;
          gap: 0.3rem;
          margin: 0.2rem 0.75rem;
          
          .skeleton-tag {
            width: 3rem;
            height: 0.8rem;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: skeleton-loading 1.5s ease-in-out infinite;
            border-radius: 4px;
          }
        }
        
        .skeleton-price {
          height: 1.125rem;
          width: 4rem;
          margin: 0.25rem 0.75rem 0.3125rem;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
        }
      }
    }
  }
  
  // 淡入淡出过渡动画
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  
  .fade-enter {
    opacity: 0;
    transform: translateY(10px);
  }
  
  .fade-enter-to {
    opacity: 1;
    transform: translateY(0);
  }
  
  .fade-leave {
    opacity: 1;
    transform: translateY(0);
  }
  
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  // 返回顶部按钮
  .back-to-top {
    position: fixed;
    right: 20px;
    bottom: 80px; // 在底部导航栏上方
    width: 33px;
    height: 33px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 999;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &:hover {
      background: rgba(0, 0, 0, 0.8);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

}

/* 根据主题色动态设置分类背景（通过内联style注入） */
:deep(.category-section){
  background: transparent; /* 实际背景由内联style控制 */
}
</style>